import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "firstName",
    "lastName",
    "email",
    "phoneNumber",
    "phoneCountryCode",
    "organizationName",
    "password",
    "passwordConfirmation",
    "terms",
    "submitButton",
  ];

  static values = {
    minimumPasswordLength: { type: Number, default: 6 },
  };

  connect() {
    this.touchedFields = new Set();
    this.validate();
  }

  validate(event) {
    if (event?.target) {
      this.touchedFields.add(event.target);
    }

    const validations = [
      this.validateHumanName(this.firstNameTarget, "First name"),
      this.validateHumanName(this.lastNameTarget, "Last name"),
      this.validateEmail(),
      this.validatePhone(),
      this.validateOrganizationName(),
      this.validatePassword(),
      this.validatePasswordConfirmation(),
      this.validateTerms(),
    ];

    const isValid = validations.every(Boolean);

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !isValid;
    }

    return isValid;
  }

  submit(event) {
    this.allFields.forEach((field) => this.touchedFields.add(field));

    if (this.validate()) {
      return;
    }

    event.preventDefault();
    this.firstInvalidField?.focus();
  }

  validateHumanName(field, label) {
    const value = field.value.trim();
    let message = "";

    if (value.length === 0) {
      message = `${label} is required.`;
    } else if (value.length < 2) {
      message = `${label} must be at least 2 characters.`;
    } else if (!/^[\p{L}][\p{L}\p{M}' -]*[\p{L}\p{M}]$/u.test(value)) {
      message = `${label} must look like a real name.`;
    }

    return this.setFieldState(field, message);
  }

  validateEmail() {
    const field = this.emailTarget;
    const value = field.value.trim().toLowerCase();
    const domain = value.split("@").pop();
    let message = "";

    if (value.length === 0) {
      message = "Work email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      message = "Enter a valid work email address.";
    } else if (this.personalEmailDomains.includes(domain)) {
      message = "Use your work email address.";
    } else if (this.suspiciousEmailDomains.includes(domain)) {
      message = "That email domain looks misspelled.";
    } else if (this.isDisposableEmailDomain(domain)) {
      message = "Temporary or disposable email addresses are not allowed.";
    }

    return this.setFieldState(field, message);
  }

  validatePhone() {
    const field = this.phoneNumberTarget;
    const countryCode = this.hasPhoneCountryCodeTarget ? this.phoneCountryCodeTarget.value : "";
    const digits = `${countryCode}${field.value}`.replace(/\D/g, "");
    let message = "";

    if (field.value.trim().length === 0) {
      message = "Phone number is required.";
    } else if (digits.length < 7 || digits.length > 15) {
      message = "Enter a valid phone number.";
    } else if (/^(\d)\1+$/.test(digits)) {
      message = "Enter a real phone number.";
    }

    return this.setFieldState(field, message);
  }

  validateOrganizationName() {
    const field = this.organizationNameTarget;
    const value = field.value.trim();
    const userNames = [
      this.firstNameTarget.value.trim().toLowerCase(),
      this.lastNameTarget.value.trim().toLowerCase(),
      `${this.firstNameTarget.value.trim()} ${this.lastNameTarget.value.trim()}`.trim().toLowerCase(),
    ].filter(Boolean);
    let message = "";

    if (value.length === 0) {
      message = "Organization name is required.";
    } else if (value.length < 2) {
      message = "Organization name must be at least 2 characters.";
    } else if (value.length > 100) {
      message = "Organization name must be 100 characters or less.";
    } else if (!/^[\p{L}\p{N}\s\-'.,&()]+$/u.test(value)) {
      message = "Use only letters, numbers, spaces, and common punctuation.";
    } else if (!/\p{L}/u.test(value)) {
      message = "Organization name must include at least one letter.";
    } else if (userNames.includes(value.toLowerCase())) {
      message = "Enter your company or organization name.";
    }

    return this.setFieldState(field, message);
  }

  validatePassword() {
    const field = this.passwordTarget;
    let message = "";

    if (field.value.length === 0) {
      message = "Password is required.";
    } else if (field.value.length < this.minimumPasswordLengthValue) {
      message = `Password must be at least ${this.minimumPasswordLengthValue} characters.`;
    }

    return this.setFieldState(field, message);
  }

  validatePasswordConfirmation() {
    const field = this.passwordConfirmationTarget;
    let message = "";

    if (field.value.length === 0) {
      message = "Confirm your password.";
    } else if (field.value !== this.passwordTarget.value) {
      message = "Passwords do not match.";
    }

    return this.setFieldState(field, message);
  }

  validateTerms() {
    const field = this.termsTarget;
    const message = field.checked ? "" : "Accept the terms to continue.";

    return this.setFieldState(field, message);
  }

  setFieldState(field, message) {
    const isTouched = this.touchedFields.has(field);
    const showError = isTouched && message;
    const messageElement = this.messageElementFor(field);

    field.setAttribute("aria-invalid", showError ? "true" : "false");
    field.classList.toggle("border-red-500", showError);
    field.classList.toggle("dark:border-red-400", showError);
    field.classList.toggle("focus:border-red-500", showError);
    field.classList.toggle("dark:focus:border-red-400", showError);
    field.classList.toggle("focus:ring-red-500", showError);
    field.classList.toggle("dark:focus:ring-red-400", showError);

    messageElement.textContent = showError ? message : "";
    messageElement.classList.toggle("hidden", !showError);

    return message.length === 0;
  }

  messageElementFor(field) {
    if (field.dataset.signupValidationMessageId) {
      return document.getElementById(field.dataset.signupValidationMessageId);
    }

    const id = `${field.id || field.name.replace(/\W/g, "_")}_validation_message`;
    const message = document.createElement("p");
    message.id = id;
    message.className = "mt-1 hidden text-xs text-red-600 dark:text-red-400";

    field.setAttribute("aria-describedby", [field.getAttribute("aria-describedby"), id].filter(Boolean).join(" "));
    field.dataset.signupValidationMessageId = id;

    this.messageContainerFor(field).insertAdjacentElement("afterend", message);

    return message;
  }

  messageContainerFor(field) {
    const explicitContainer = field.closest("[data-signup-validation-message-container]");

    if (explicitContainer) {
      return explicitContainer;
    }

    const passwordWrapper = field.closest("[data-controller='password-visibility']");

    if (passwordWrapper) {
      return passwordWrapper;
    }

    if (field === this.phoneNumberTarget) {
      return field.closest(".mb-6") || field;
    }

    return field;
  }

  get allFields() {
    return [
      this.firstNameTarget,
      this.lastNameTarget,
      this.emailTarget,
      this.phoneNumberTarget,
      this.organizationNameTarget,
      this.passwordTarget,
      this.passwordConfirmationTarget,
      this.termsTarget,
    ];
  }

  get firstInvalidField() {
    return this.allFields.find((field) => field.getAttribute("aria-invalid") === "true");
  }

  get personalEmailDomains() {
    return [
      "gmail.com",
      "yahoo.com",
      "yahoo.co.uk",
      "yahoo.fr",
      "hotmail.com",
      "hotmail.fr",
      "hotmail.co.uk",
      "outlook.com",
      "outlook.fr",
      "live.com",
      "msn.com",
      "aol.com",
      "icloud.com",
      "me.com",
      "mac.com",
      "mail.com",
      "inbox.com",
      "gmx.com",
      "zoho.com",
      "protonmail.com",
      "proton.me",
      "tutanota.com",
      "yandex.com",
      "mail.ru",
    ];
  }

  get suspiciousEmailDomains() {
    return [
      "gmal.com",
      "gmail.co",
      "gmail.con",
      "gmail.cim",
      "gnail.com",
      "hotnail.com",
      "outlook.con",
      "outlook.cim",
      "yahho.com",
      "yaho.com",
      "yahoo.co",
      "yahoo.con",
      "yahoo.cim",
      "yahonn.cim",
    ];
  }

  isDisposableEmailDomain(domain) {
    return this.disposableEmailDomains.some(
      (blockedDomain) => domain === blockedDomain || domain.endsWith(`.${blockedDomain}`),
    );
  }

  get disposableEmailDomains() {
    return [
      "account.xn--yaho-sqa.com",
      "ambasing.web.id",
      "arbitcore.in",
      "asiatic.web.id",
      "best.xn--yaho-sqa.com",
      "bobladn.opik.net",
      "bubu.edu.tekgo.cifonice.my.id",
      "custo.linkpc.net",
      "desiredprofiles.com",
      "directdeal.click",
      "edu.xn--yaho-sqa.com",
      "eduxflow.qzz.io",
      "eradrop.cloud",
      "hgpmedia.co.uk",
      "noriina.shop",
      "op.xn--yaho-sqa.com",
      "orangeosdz.online",
      "photoshub.work.gd",
      "pmo.qzz.io",
      "qwe.web.id",
      "rbesar.info",
      "samirb.com.np",
      "swapgaming.com.ar",
      "videa.cam",
      "walker-visuals.run.place",
      "wiretalk.net",
      "xn--yaho-sqa.com",
    ];
  }
}
